import { get as lodash_get } from "lodash-es";
import { isValidElement } from "react";
import ReactDOMServer from "react-dom/server";
import env from "vendor/webix/sources/webix/env";
import { $active } from "vendor/webix/sources/webix/skin";
import * as webix from "vendor/webix/webix";

import checksuggest from "vendor/webix/sources/views/checksuggest";
import datafilter from "vendor/webix/sources/ui/datafilter";

import { _to_array } from "vendor/webix/sources/webix/helpers";

import "common/components/webix/webix.scss";

import { getTextSizeFast } from "vendor/webix/getTextSizeFast";

webix.env.cdn = "https://cdn.pazien.com/webix";
webix.env.zIndexBase = 2000;
webix.csv.delimiter.cols = ",";

/**
 * Our custom unit list to fix stupidity of Webix when storing groups in a hash.
 * It doesn't allow ordering of keys inside, but we need to put Rows above Values...
 */
webix.protoUI({
    name: "orderedunitlist",

    _setUnits() {
        const list = this;
        this.units = {};
        this.data.each(function(obj) {
            const result = list._settings.uniteBy.call(this, obj);
            obj.$unitValue = result;
            if (!list.units[result]) { list.units[result] = []; }
            list.units[result].push(obj.id);
        });

        // Apply new order of keys to this.units
        this.units = this._settings.unitsOrder.reduce((memo, unit) => {
            if (this.units[unit]) {
                memo[unit] = this.units[unit];
            }
            return memo;
        }, {});
    }
}, webix.ui.unitlist);

webix.protoUI({
    name: "checkbox",
    $setValue(value) {
        if (this.$destructed) return;
        const isChecked = (value === this._settings.checkValue);
        const input = this.$view.getElementsByTagName("input")[0];
        const parentNode = input ? input.parentNode : null;
        
        if (parentNode && this._settings.customCheckbox) {
            const button = parentNode.getElementsByTagName("BUTTON");
            if (button[0]) button[0].setAttribute("aria-checked", isChecked ? "true" : "false");
        }
        if (parentNode) {
            parentNode.className = parentNode.className.replace(/(webix_checkbox_)\d/, `$1${(isChecked ? 1 : 0)}`);
        }
        input.checked = isChecked;
    }
}, webix.ui.checkbox);

webix.toHTML = function(viewId) {
    const view = webix.$$(viewId);

    const tableData = [];
    const columns = view.config.columns.filter(({ id }) => id !== "ch1" && id !== "recon3actions");

    /*
    Split the first column of tables with n pivots into n columns
    So this:

    | pivot1_header > pivot2_header > pivot3_header | column1 | column2 |
    |-----------------------------------------------+---------+---------|
    | pivot1                                        |       3 | ...     |
    | pivot2_1                                      |       2 | ...     |
    | pivot3_1                                      |       2 | ...     |
    | pivot 2_2                                     |       1 | ...     |
    | pivot1_2                                      |       5 | ...     |

    transposes into this:

    | pivot1_header | pivot2_header | pivot3_header | column1 | column2 |
    |---------------+---------------+---------------+---------+---------|
    | pivot1        |               |               |       3 | ...     |
    |               | pivot2_1      |               |       2 | ...     |
    |               |               | pivot3_1      |       2 | ...     |
    |               | pivot 2_2     |               |       1 | ...     |
    | pivot1_2      |               |               |       5 | ...     |

    without affecting tables which do not have pivots (their objects do not have the `$level` field).
    */

    // Pivot columns is the number of levels of pivots:
    // for tables that are not pivoted, the level is 1. For the above example, the level is 3.
    let pivotColumns = 1;
    // eslint-disable-next-line unicorn/prefer-array-some, no-loop-func
    while (view.find((obj) => obj.$level === pivotColumns + 1, true)) pivotColumns++;

    const placeHolderCell = { txt: "", span: { colspan: 1, rowspan: 1 }, placeholder: true };

    view.eachRow((row) => {
        // prepend enough columns to accommodate all pivots and all data values.
        const colrow = Array.from({ length: pivotColumns + columns.length - 1 }).fill(placeHolderCell);

        // level is > 1 for pivot table rows. Use the level as the offset into the output data.
        const levelIndex = view.getItem(row).$level - 1 || 0;
        columns.forEach(({ id: columnId }, index) => {
            const cell = { txt: view.getText(row, columnId) || "", span: null };
            if (index === 0) {
                // Put the pivot value into the correct column (or the first if there are no pivots);
                colrow[levelIndex] = cell;
            } else {
                // All other columns have to start /after/ all pivot columns.
                colrow[pivotColumns - 1 + index] = cell;
            }
        });

        tableData.push(colrow);
    });

    // Handle tables without data
    if (tableData.length === 0) {
        tableData.push([]);
    }

    // _getTableHeader normally prepends the header onto the full table, but since we need to process, hack the call to just get the header row(s).
    let tableHeader = view._getTableHeader([[tableData[0].filter(({ placeholder }) => !placeholder)]], columns, "header")[0].slice(0, -1);

    if (pivotColumns > 1) {
        /*
        There are cases when a pivot table has nested columns, in which case the header structure in webix is:

        |                          | column1                           | column2                           | // <- tableHeader[0]
        | pivot1 > pivot2 > pivot3 +-----------------------------------+-----------------------------------|
        |                          | column1_1 | column1_2 | column1_3 | column2_1 | column2_2 | column2_3 | // <- tableHeader[1]
        |--------------------------+-----------+-----------+-----------+-----------+-----------+-----------|
        | data                     | data      | data      | data      | data      | data      | data      | // <- tableData[0]

        in such cases, the header should transpose to:
        |        |        |        | column1                           | column2                           | // <- tableHeader[0]
        |--------+--------+--------+-----------------------------------+-----------------------------------|
        | pivot1 | pivot2 | pivot3 | column1_1 | column1_2 | column1_3 | column2_1 | column2_2 | column2_3 | // <- tableHeader[1]
        |--------+--------+--------+-----------+-----------+-----------+-----------+-----------+-----------|
        | data   |        |        | data      | data      | data      | data      | data      | data      | // <- tableData[0]

          */
        // If there is a pivot, then the header on the first column will look like `pivot1 > pivot2 > pivot3 ....`
        const pivotHeader = tableHeader[0][0];
        // take the pivotHeader out, it will be replaced.
        tableHeader[0][0] = placeHolderCell;

        // Our convention is for pivot tables to separate pivot headers with " > ".
        // if this isn't the case then just replicate the full header for every pivot column.
        let splitPivotHeader = Array.from({ length: pivotColumns }).fill({ ...pivotHeader, span: { colspan: 1, rowspan: 1 } });
        if (typeof pivotHeader.txt === "string") {
            // get rid of any markup
            const pivotHeaderText = pivotHeader.txt.replace(/(<([^>]+)>)/gi, "");
            if ((pivotHeaderText.match(/\s>\s/g) || []).length === pivotColumns - 1) {
                // If the pivotHeader is correctly formatted, split it into the separate cells.
                splitPivotHeader = pivotHeaderText.split(" > ").map((txt) => ({ ...pivotHeader, span: { colspan: 1, rowspan: 1 }, txt }));
            }
        }

        // every header row gets n empty cells prepended to it, except the last header row, which gets the split out pivotHeader.
        tableHeader = tableHeader.map((row, index) => [
            ...index === tableHeader.length - 1 ? splitPivotHeader : Array.from({ length: pivotColumns }).fill(placeHolderCell),
            ...row.slice(1)
        ]);
    }

    const tableDataWithHeader = [...tableHeader, ...tableData];

    // Create a table element and populate it with rows and cells
    const tableHTML = document.createElement("table");

    tableDataWithHeader.forEach((row) => {
        const tr = document.createElement("tr");
        row.forEach((cell) => {
            // empty cells indicate a preceding cell has either a rowspan or colspan > 1. i.e. this cell doesn't need to render.
            if (!cell) return;
            const td = document.createElement("td");
            // Remove empty html tags from cell.txt
            // https://github.com/Pazien/web-application/issues/4760
            td.innerText = typeof cell.txt === "string" ? cell.txt.replace(/(<([^>]+)>)/gi, "").trim() : cell.txt;
            
            if (cell.span) {
                td.colSpan = cell.span.colspan;
                td.rowSpan = cell.span.rowspan;
            }

            tr.appendChild(td);
        });

        tableHTML.appendChild(tr);
    });

    return tableHTML;
};

// Upgrade webix datatable to use our faster implementation for getTextSize
// webapplication/static/js/vendor/webix/sources/views/datatable/size.js
try {
    const mixin = webix.ui.datatable.$protoWait.find((i) => i._getColumnConfigSize);
    mixin._getColumnConfigSize = function(ind, headers) {
        const config = this._settings.columns[ind];
        let max = config.minWidth || this._settings.minColumnWidth;

        // get max data width
        if (headers !== "header") {
            let count = this.data.order.length;
            if (config.adjustBatch && config.adjustBatch < count) count = config.adjustBatch;
            const order = this.data.order.slice(0, count);

            for (let i = 0; i < count; i++) order[i] = order[i] ? this._getValue(this.getItem(order[i]), config, 0) : "";
            max = Math.max(max, getTextSizeFast(order, "webix_table_cell webix_cell", void 0, true).width);
        }

        if (headers !== "data") {
            // Webix aplies max width to the second column. If first colspan is larger then second row header would get larger value too.
            // Trick here is to only use the last column to compute width, but only if the header contains more than one column under a parentGroup
            // Issue: https://snippet.webix.com/zma1so52
            let startColumnIdx = 0;
            if (config.packCols) {
                const totalChildren = this._settings.columns.filter((column) => column.parentGroup === config.parentGroup);
                if (typeof config.parentGroup !== "undefined" && totalChildren.length > 1) {
                    // Start with the last row in the header
                    startColumnIdx = config.header.length - 1;
                }
            }
            for (; startColumnIdx < config.header.length; startColumnIdx++) {
                const header = config.header[startColumnIdx];
                if (header) {
                    let width = 0;
                    if (header.rotate) for (let h = 0; h < (header.rowspan || 1); h++) width += this._headers[h];
                    const css = `webix_table_cell webix_cell ${header.css || ""}${header.rotate ? "webix_measure_rotate" : ""}`;
                    const text = header.css === "webix_div_filter" ? `<div class="flex-content">${header.inputConfig.label}<div style="width: 42px; height: 32px"></div></div>` : header.text;
                    const size = getTextSizeFast([text], css, width);
                    max = Math.max(max, header.rotate ? size.height : size.width);
                }
            }
            if (config.sort) max += 10; // add 10px for sort marker
        }

        max = max + (env.isIE ? $active.layoutPadding.space : 0);

        return Math.min(max, config.maxWidth || this._settings.maxColumnWidth || 100_000);
    };
} catch (error) {
    console.error(error);
    throw new Error(error);
}

// Upgrade webix datatable to run custom code after adding a span
// This code may break in the future on webix upgrade.
// But since it's executed at the app init, we should be able to catch the issue earlier
try {
    const mixin = webix.ui.datatable.$protoWait.find((i) => i._add_span_to_area);
    const original = mixin._add_span_to_area;
    mixin._add_span_to_area = function(...args) {
        original.apply(this, args);
    };
} catch (error) {
    console.error(error);
    throw new Error(error);
}

// Fix issue in IE11 in tables with column filters
try {
    const mixin = webix.ui.text.$protoWait.find((i) => i.$getValue);
    mixin.$getValue = function() {
        try {
            const node = this.getInputNode();
            const value = node?.value;
            return this._pattern(value, false);
        } catch (error) {
            console.log(error);
        }
    };
} catch (error) {
    console.error(error);
    throw new Error(error);
}

// Upgrade webix datatable to add custom css class when column resize mode is enabled/disabled
// We need this to override our custom css that sets cursor:pointer to each header
try {
    const mixin = webix.ui.datatable.$protoWait.find((i) => i._mark_resize_ready);
    const original = mixin._mark_resize_ready;
    mixin._mark_resize_ready = function(mode) {
        if (this._last_cursor_mode !== mode) {
            this._viewobj.classList.remove(`pz-${this._last_cursor_mode}`);
            this._viewobj.classList.add(`pz-${mode || "default"}`);
        }
        original.call(this, mode);
    };
} catch (error) {
    console.error(error);
    throw new Error(error);
}

// Upgrade webix datatable to use lodash get in getValue method
try {
    const mixin = webix.ui.datatable.$protoWait.find((i) => i._getValue);
    mixin._getValue = function(item, config, i) {
        if (!item) return "";

        let value = lodash_get(item, config.id);

        if (value === void 0 || value === null) value = "";

        if (config.format) value = config.format(value);

        if (config.template) value = config.template(item, this.type, value, config, i);

        if (isValidElement(value)) {
            value = ReactDOMServer.renderToStaticMarkup(value);
        }

        return value;
    };
} catch (error) {
    console.error(error);
    throw new Error(error);
}

// Upgrade webix datatable to use custom showOverlay method, that accepts React
try {
    const mixin = webix.ui.datatable.$protoWait.find((i) => i.showOverlay);
    mixin.showOverlay = function(message) {
        if (!this._datatable_overlay) {
            const t = document.createElement("div");
            t.setAttribute("class", "webix_overlay");
            this._body.appendChild(t);
            this._datatable_overlay = t;
        }
        
        if (typeof message === "string") {
            this._datatable_overlay.innerText = message;
        }
        
        if (isValidElement(message)) {
            this._datatable_overlay.innerHTML = ReactDOMServer.renderToString(message);
        }
    };
} catch (error) {
    console.error(error);
    throw new Error(error);
}

// Update webix input to not render inline onclick attribute to prevent throwing CSP error
try {
    const originalProto = webix.ui.text.$protoWait.find((i) => i._render_div_block);
    originalProto._render_div_block = function(obj, common) {
        const height = this._settings.aheight - 2 * webix.skin.$active.inputPadding - 2 * this._borderWidth;
        const text = obj.text || obj.value || this._get_div_placeholder(obj);
        const html = `<div class="webix_inp_static"
                           role="combobox"
                           aria-label="${webix.template.escape(obj.label)}"
                           tabindex="0"
                           ${obj.readonly ? " aria-readonly='true'" : ""}
                           ${obj.invalid ? "aria-invalid='true'" : ""}
                           style="line-height:${height}px; width: ${common._get_input_width(obj)}px; text-align: ${obj.inputAlign || "left"};">
                        ${text}
                       </div>`;
        return common.$renderInput(obj, html, `x${webix.uid()}`);
    };

    // Same shit in the label html. We need to remove onclick
    originalProto.$renderLabel = function(config, id) {
        let label = "";

        if (config.label) {
            const labelAlign = config.labelAlign || "left";
            const top = this._settings.labelPosition === "top";
            const labelTop = top ? "display:block;" : `width: ${this._settings.labelWidth}px;`;

            const labelHeight = this._getLabelHeight(top);

            label = `<label style="${labelTop}text-align: ${labelAlign};line-height:${labelHeight}px;" for="${id}" class="webix_inp_${top ? "top_" : ""}label ${config.required ? "webix_required" : ""}">${config.label || ""}</label>`;
        }

        return label;
    };
} catch (error) {
    console.error("Unable to patch webix.ui.text");
    console.error(error);
    throw new Error(error);
}

webix.protoUI({
    name: "sortsuggest",
    defaults: {
        button: false,
        selectAll: false,
        sortOptions: false,
        body: {
            rows: [
                {
                    view: "list",
                    css: "sortsuggest-sort-list",
                    hidden: true,
                    template: "<i class='#icon#'></i> <span class='sort-title'>#title#</span>",
                    select: true,
                    height: 72,
                    data: [
                        { id: "asc", title: "Ascending (A - Z)", icon: "fal fa-sort-alpha-down" },
                        { id: "desc", title: "Descending (Z - A)", icon: "fal fa-sort-alpha-down-alt" }
                    ],
                    on: {
                        onItemClick(sort) {
                            const master = webix.$$(this.getParentView().getParentView()._settings.master);
                            master.applySort(sort, this);
                        }
                    }
                },
                {
                    ...checksuggest.api.defaults.body.rows[0],
                    customCheckbox: true,
                    borderless: false,
                    css: "webix_list_item pazien-filter-select-all"
                },
                {
                    ...checksuggest.api.defaults.body.rows[1],
                    on: {
                        onItemClick(id, e) {
                            const item = this.getItem(id);
                            item.$checked = item.$checked ? 0 : 1;
                            this.refresh(id);

                            const popup = this.getParentView().getParentView();
                            popup._click_stamp = new Date();

                            popup._toggleOption(id, e);

                            if (popup.config.selectAll) {
                                if (!item.$checked) {
                                    popup.getSelectAll().setValue(0);
                                } else if (popup._is_all_selected()) {
                                    popup.getSelectAll().setValue(1);
                                }
                            }
                        }
                    }
                },
                checksuggest.api.defaults.body.rows[2]
            ]
        }
    },

    _set_on_popup_click() {
        const button = this.getButton();
        if (button) {
            const text = this._settings.button ? this._settings.buttonText || webix.i18n.combo.select : 0;
            if (text) {
                button._settings.value = text;
                button.refresh();
            } else button.hide();
        }

        if (this._settings.sortOptions) {
            this.getSort().show();
        }

        if (this._settings.selectAll) {
            return this.getSelectAll().show();
        }

        const list = this.getList();
        // for standalone suggests we need to have a normal show/hide logic
        // use a wrapper function, so it can later be cleared in multicombo
        list.data.attachEvent("onAfterFilter", () => this._suggest_after_filter());
    },

    _onReady() {
        const list = this.getList();
        if (list.config.dataFeed) {
            const suggest = this;
            list.attachEvent("onAfterLoad", () => {
                suggest.setValue(suggest._settings.value);
            });
            list.getItem = function(id) {
                return this.data.pull[id] || suggest._valueHistory[id];
            };
        }

        if (this.config.master && !this.config.selectAll) {
            this.getSelectAll().hide();
        }
    },

    _is_all_selected() {
        if (this.$destructed) return false;
        const value = this.getValue();
        const all = value ? value.split(this.config.separator) : [];
        return (all.length === this.getList().count());
    },

    _toggleOption(id, ev, all) {
        const value = this.getValue();
        const values = all || _to_array(value ? this.getValue().split(this._settings.separator) : []);
        const master = webix.$$(this._settings.master);

        if (!all) {
            if (values.find(id) < 0) values.push(id);
            else values.remove(id);
        }

        const data = values.join(this._settings.separator);
        const text = this.setValue(values).join(this._settings.separator);
        if (master) master.setValue(data);
        else if (this._last_input_target) this._last_input_target.value = text;

        this.callEvent("onValueSuggest", [{ id: data, text }]);

        if (ev) { // only for clicks in checksuggest
            if (this.getList().$destructed) return; // escape if component is destructured
            const checkbox = this.getList().getItemNode(id).getElementsByTagName("SPAN");

            if (checkbox && checkbox.length) checkbox[0].focus();
        }
    },

    getSort() {
        return this.getBody().getChildViews()[0];
    },

    getSelectAll() {
        return this.getBody().getChildViews()[1];
    },

    getList() {
        return this.getBody().getChildViews()[2];
    },

    getButton() {
        return this.getBody().getChildViews()[3];
    }
}, webix.ui.checksuggest);

webix.protoUI({
    name: "buttonMultiSelect",

    _suggest_config(value) {
        const suggest = {
            view: "sortsuggest",
            separator: this.config.separator,
            buttonText: this.config.buttonText,
            button: this.config.button
        };

        if (this._settings.optionWidth) {
            suggest.width = this._settings.optionWidth;
        } else {
            suggest.fitMaster = true;
        }

        const isobj = !Array.isArray(value) && typeof value === "object" && !value.name;
        if (isobj) {
            webix.extend(suggest, value, true);
        }

        const view = webix.ui(suggest);
        const list = view.getList();
        if (typeof value === "string") list.load(value);
        else if (!isobj) list.parse(value);

        view.attachEvent("onShow", () => {
            view.setValue(webix.$$(view._settings.master).config.value);
        });

        return view;
    },

    $setValue(value) {
        if (!this._rendered_input) return;

        const popup = this.getPopup();
        let text = "";
        if (popup) {
            text = popup.setValue(value);
            if (typeof text === "object") {
                text = text.join(`${this.config.separator} `);
            }
        }
        this._settings.text = text;
        // Do not set back the value as html
    },

    applySort(sort, list) {
        this.callEvent("onSortChange", [{
            sort,
            list,
            columnId: this.columnId
        }]);
    },

    getText() {
        return this._settings.label;
    }
}, webix.ui.multiselect);

// Horrible hack to allow multiple filtered columns to be assigned values when initialized.
// Notes in refresh and render methods below.
const pazienSelectFilterInitialized = {};

webix.ui.datafilter.pazienSelectFilter = webix.extend({
    inputtype: "buttonMultiSelect",

    refresh(master, node, value) {
        if (master.$destructed) return;

        // NOTE: on first call for a column (assumption that contentId is already set and unique to one column), indicate initialize start.
        if (!pazienSelectFilterInitialized[value.contentId]) pazienSelectFilterInitialized[value.contentId] = false;

        const select = webix.$$(value.richselect);

        // Store column id insude the select
        select.columnId = value.columnId;

        // IE11 can destroy the content of richselect, so recreating
        if (!select.$view.parentNode) {
            const d = webix.html.create("div", { "class": "webix_richfilter" });
            d.appendChild(select.$view);
        }

        node.$webix = value.richselect;

        value.compare = value.compare || this.compare;
        value.prepare = value.prepare || this.prepare;
        master.registerFilter(node, value, this);

        const data = datafilter._get_data(master, value);
        const list = select.getPopup().getList();

        // reattaching node back to master container
        node.appendChild(select.$view.parentNode);

        // load data in list, must be after reattaching, as callback of parse can try to operate with innerHTML
        if (list.parse) {
            list.clearAll();
            list.parse(value.template ? data.map(value.template) : data);

            if ((!this.$noEmptyOption && value.emptyOption !== false) || value.emptyOption) {
                const emptyOption = { id: "$webix_empty", value: value.emptyOption || "", $empty: true };
                list.add(emptyOption, 0);
            }
        }

        // Set actual value for the filter.
        // NOTE: Internally calls the attached `onChange` handler, which should call `master.filterByAll` when filtering on 2 or more columns.
        // If filterByAll is called before the second column has called `setValue` when initializing, the value is wiped.
        // Avoid that with the initialization checks.
        if (value.value) this.setValue(node, value.value);

        // repaint the filter control
        select.render();

        // adjust sizes after full rendering
        webix.delay(select.resize, select);

        // NOTE: When refresh is called once, the value is set for each column.
        // On subsequent calls all columns are already set, and filtering is safe.
        pazienSelectFilterInitialized[value.contentId] = true;
    },

    render(master, config) {
        if (!config.richselect) {
            const d = webix.html.create("div", { "class": "webix_richfilter pazien_select_filter" });

            const richconfig = {
                container: d,
                view: "buttonMultiSelect",
                template(obj, common) {
                    const text = obj.text || obj.value.length;
                    const classNames = ["webix_inp_static", "pazien-filter-selector"];

                    obj.icon = "wxi-menu-down";
                    if (text) {
                        classNames.push("has-filter-applied");
                        obj.icon = "fal fa-filter";
                    }

                    const html = `<div class="${classNames.join(" ")}" role="combobox" tabindex="0" style="line-height:20px; width: 20px;"></div>`;

                    return common.$renderInput(obj, html, `x${webix.uid()}`);
                },
                options: []
            };

            const inputConfig = webix.extend(this.inputConfig || {}, config.inputConfig || {}, true);
            webix.extend(richconfig, inputConfig);

            if (config.separator) {
                richconfig.separator = config.separator;
            }

            if (config.suggest) {
                richconfig.suggest = config.suggest;
            }

            const richselect = webix.ui(richconfig);

            richselect.attachEvent("onChange", () => {
                // NOTE: Only call the attached handler if the column has completely finished initializing (meaning its peers have too).
                if (pazienSelectFilterInitialized[config.contentId] !== false) {
                    config.onChange(master, ...arguments);
                }
            });

            richselect.attachEvent("onSortChange", ({ sort, columnId, list }) => {
                // We should unselect all other items in other popups
                if (this._activeSortList && !this._activeSortList.$destructed) {
                    this._activeSortList.unselectAll();
                }

                // Set new active sort list
                this._activeSortList = list;

                master.sort({
                    as: master.config.columns.find(({ id }) => id === columnId).sort,
                    dir: sort,
                    by: columnId
                });

                master.markSorting(columnId, sort);
            });

            config.richselect = richselect._settings.id;
            master._destroy_with_me.push(richselect);
        }

        config.css = "webix_div_filter";
        return " ";
    },

    compare(a, b) {
        if (Array.isArray(a)) {
            return !b || Object.keys(b).some((key) => a.includes(key));
        }
        return !b || b[a];
    }

}, webix.ui.datafilter.multiSelectFilter);

// We have to export as default here. Otherwise other webix widgets won't load because they did not set them as modules 🤦‍♂️

// eslint-disable-next-line import/no-default-export
export { webix };
